import { type FormEvent, type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Badge, Button, Card, Input, Skeleton, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateSender,
  useDeleteSender,
  useSendersList,
  useUpdateSenderStatus,
  type CreateSenderInput,
  type RegistrationStatus,
  type SenderItem,
  type SenderType,
} from '@/hooks/useSenders';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

const SENDER_TYPES: SenderType[] = ['long_code', 'toll_free', 'short_code', 'alphanumeric'];
const STATUSES: RegistrationStatus[] = [
  'not_required',
  'pending',
  'submitted',
  'approved',
  'rejected',
  'suspended',
];

interface FormState {
  type: SenderType;
  value: string;
  country_code: string;
  use_case: string;
  initial_status: RegistrationStatus;
}

const INITIAL_FORM: FormState = {
  type: 'long_code',
  value: '',
  country_code: 'US',
  use_case: '',
  initial_status: 'not_required',
};

interface FieldErrors {
  value?: string;
  country_code?: string;
}

function statusVariant(status: RegistrationStatus): 'success' | 'danger' | 'warn' | 'info' | 'gray' {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
    case 'suspended':
      return 'danger';
    case 'pending':
    case 'submitted':
      return 'warn';
    case 'not_required':
      return 'info';
    default:
      return 'gray';
  }
}

interface RowProps {
  s: SenderItem;
  lang: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onChangeStatus: (id: number, status: RegistrationStatus) => void;
  onDelete: (id: number) => void;
  busy: boolean;
}

function SenderRow({ s, lang, t, onChangeStatus, onDelete, busy }: RowProps): JSX.Element {
  const [confirmDel, setConfirmDel] = useState(false);
  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2">
      <td className="px-5 py-3 num text-text whitespace-nowrap">{s.value}</td>
      <td className="px-5 py-3 text-text-2">{t(`sender_type_${s.type}`)}</td>
      <td className="px-5 py-3 num text-text-2 uppercase">{s.country_code}</td>
      <td className="px-5 py-3">
        <Badge variant={statusVariant(s.registration_status)} className="gap-1.5">
          {t(`reg_status_${s.registration_status}`)}
        </Badge>
      </td>
      <td className="px-5 py-3 text-text-3 whitespace-nowrap">
        <span title={new Date(s.created_at).toLocaleString(lang)}>
          {formatRelativeTime(s.created_at, lang)}
        </span>
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-2">
          <select
            value={s.registration_status}
            onChange={(e) => onChangeStatus(s.id, e.target.value as RegistrationStatus)}
            disabled={busy}
            className="h-8 px-2 text-xs bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 disabled:opacity-50"
            title={t('senders_change_status')}
          >
            {STATUSES.map((st) => (
              <option key={st} value={st}>
                {t(`reg_status_${st}`)}
              </option>
            ))}
          </select>
          {confirmDel ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setConfirmDel(false)}
                disabled={busy}
              >
                {t('senders_cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => onDelete(s.id)}
                disabled={busy}
                className="bg-danger text-white hover:bg-danger/90"
              >
                {t('senders_confirm_delete')}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setConfirmDel(true)}
              disabled={busy}
            >
              {t('senders_delete')}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// KPI strip + brand-verification banner — used only on this page so they
// live inline rather than in components/ui.
// ---------------------------------------------------------------------------

interface SenderKpiProps {
  label: string;
  value: number;
  accent: 'success' | 'warn' | 'danger';
  loading: boolean;
  lang: string;
}

function SenderKpi({ label, value, accent, loading, lang }: SenderKpiProps): JSX.Element {
  const accentClass =
    accent === 'success' ? 'bg-success' : accent === 'warn' ? 'bg-warn' : 'bg-danger';
  const valueClass =
    accent === 'success' ? 'text-success' : accent === 'warn' ? 'text-warn' : 'text-danger';
  return (
    <Card padding="md" className="relative overflow-hidden">
      <span aria-hidden="true" className={cn('absolute top-0 left-0 right-0 h-[2px]', accentClass)} />
      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">{label}</div>
      {loading ? (
        <Skeleton className="h-8 w-16 mt-2" />
      ) : (
        <div className={cn('num display text-[28px] font-semibold mt-1.5 leading-none', valueClass)}>
          {value.toLocaleString(lang)}
        </div>
      )}
    </Card>
  );
}

export default function SendersPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const { user } = useAuth();
  // Only the platform operator can pre-mark a sender as approved.
  // For everyone else the API forces 'not_required' on POST /v1/senders
  // (and rejects US numbers entirely — they must go through the
  // /v1/admin/senders pool flow). Hide the initial-status dropdown
  // from non-platform users so the UI matches what the backend
  // actually enforces, and surface a clearer hint for the US case.
  const isPlatformAdmin = user?.tenantType === 'platform';

  const list = useSendersList();
  const create = useCreateSender();
  const updateStatus = useUpdateSenderStatus();
  const del = useDeleteSender();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const validate = (values: FormState): FieldErrors => {
    const next: FieldErrors = {};
    if (!values.value.trim()) {
      next.value = t('senders_err_value_required');
    }
    if (!/^[A-Za-z]{2}$/.test(values.country_code.trim())) {
      next.country_code = t('senders_err_country_invalid');
    }
    return next;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitOk(null);
    const fe = validate(form);
    setErrors(fe);
    if (Object.keys(fe).length > 0) return;

    const input: CreateSenderInput = {
      type: form.type,
      value: form.value.trim(),
      country_code: form.country_code.trim().toUpperCase(),
      use_case: form.use_case.trim() || undefined,
      initial_status: form.initial_status,
    };

    try {
      const created = await create.mutateAsync(input);
      setSubmitOk(t('senders_create_ok', { value: created.value }));
      setForm({ ...INITIAL_FORM, country_code: form.country_code });
    } catch (err) {
      if (err instanceof ApiError) {
        const map: Record<string, string> = {
          duplicate: t('senders_err_duplicate'),
          validation_error: err.message,
        };
        setSubmitError(map[err.code] ?? err.message ?? t('senders_err_generic'));
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError(t('senders_err_generic'));
      }
    }
  };

  const handleChangeStatus = async (id: number, status: RegistrationStatus) => {
    setActionError(null);
    try {
      await updateStatus.mutateAsync({ id, registration_status: status });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('senders_err_generic'));
    }
  };

  const handleDelete = async (id: number) => {
    setActionError(null);
    try {
      await del.mutateAsync(id);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'validation_error') {
        setActionError(t('senders_err_in_use'));
      } else {
        setActionError(err instanceof Error ? err.message : t('senders_err_generic'));
      }
    }
  };

  const items = list.data?.items ?? [];
  const busy = updateStatus.isPending || del.isPending;

  // Status rollup for the KPI strip. "In review" collapses pending + submitted
  // (the operator doesn't distinguish — both are "carrier hasn't decided yet").
  const counts = useMemo(() => {
    const items = list.data?.items ?? [];
    let approved = 0;
    let inReview = 0;
    let rejected = 0;
    for (const s of items) {
      if (s.registration_status === 'approved') approved += 1;
      else if (s.registration_status === 'pending' || s.registration_status === 'submitted')
        inReview += 1;
      else if (s.registration_status === 'rejected' || s.registration_status === 'suspended')
        rejected += 1;
    }
    return { total: items.length, approved, inReview, rejected };
  }, [list.data]);

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
          {t('nav_senders')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1">
          {counts.total === 0
            ? t('senders_sub')
            : t('senders_count', {
                total: counts.total.toLocaleString(lang),
                approved: counts.approved.toLocaleString(lang),
              })}
        </p>
      </header>

      {/* KPI strip — Approved / In review / Rejected. Mirrors the design
          handoff (screen-senders-settings.jsx). Each card has a top-edge
          accent strip in its semantic color so the operator can scan
          "what's stuck in carrier limbo" at a glance. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <SenderKpi
          label={t('senders_kpi_approved')}
          value={counts.approved}
          accent="success"
          loading={list.isLoading}
          lang={lang}
        />
        <SenderKpi
          label={t('senders_kpi_in_review')}
          value={counts.inReview}
          accent="warn"
          loading={list.isLoading}
          lang={lang}
        />
        <SenderKpi
          label={t('senders_kpi_rejected')}
          value={counts.rejected}
          accent="danger"
          loading={list.isLoading}
          lang={lang}
        />
      </div>

      {/* Create form */}
      <Card padding="lg" className="mb-6">
        <h2 className="display text-[16px] font-semibold text-text mb-4">{t('senders_new_title')}</h2>

        {submitError && (
          <div
            role="alert"
            className="flex gap-2.5 items-start px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-4"
          >
            <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
            <div className="text-[13px] text-danger leading-relaxed">{submitError}</div>
          </div>
        )}
        {submitOk && (
          <div className="flex gap-2.5 items-start px-3.5 py-3 bg-success-bg border border-success-bd rounded-md mb-4">
            <span className="text-[13px] text-success leading-relaxed">{submitOk}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="snd-value" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('senders_field_value')}
              </label>
              <Input
                id="snd-value"
                placeholder="+15551234567"
                value={form.value}
                onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))}
                error={Boolean(errors.value)}
                disabled={create.isPending}
              />
              {errors.value ? (
                <p className="text-[12px] text-danger mt-1">{errors.value}</p>
              ) : (
                <p className="text-[12px] text-text-4 mt-1">{t('senders_field_value_help')}</p>
              )}
            </div>
            <div>
              <label htmlFor="snd-country" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('senders_field_country')}
              </label>
              <Input
                id="snd-country"
                placeholder="US"
                value={form.country_code}
                onChange={(e) =>
                  setForm((p) => ({ ...p, country_code: e.target.value.toUpperCase().slice(0, 2) }))
                }
                error={Boolean(errors.country_code)}
                disabled={create.isPending}
                maxLength={2}
                className="uppercase"
              />
              {errors.country_code && (
                <p className="text-[12px] text-danger mt-1">{errors.country_code}</p>
              )}
            </div>
          </div>

          <div
            className={cn(
              'grid gap-4 mb-4',
              isPlatformAdmin ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2',
            )}
          >
            <div>
              <label htmlFor="snd-type" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('senders_field_type')}
              </label>
              <select
                id="snd-type"
                value={form.type}
                onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as SenderType }))}
                disabled={create.isPending}
                className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50"
              >
                {SENDER_TYPES.map((tp) => (
                  <option key={tp} value={tp}>
                    {t(`sender_type_${tp}`)}
                  </option>
                ))}
              </select>
            </div>
            {/* Initial-status picker is platform-only. Non-platform
                callers always submit as not_required and the API
                forces it server-side. Showing the picker to a
                customer was misleading — they could choose "Approved"
                that the backend silently rewrote. */}
            {isPlatformAdmin && (
              <div>
                <label htmlFor="snd-status" className="block text-[12px] font-medium text-text-2 mb-1.5">
                  {t('senders_field_initial_status')}
                </label>
                <select
                  id="snd-status"
                  value={form.initial_status}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, initial_status: e.target.value as RegistrationStatus }))
                  }
                  disabled={create.isPending}
                  className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-50"
                >
                  {STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {t(`reg_status_${st}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="snd-usecase" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('senders_field_use_case')}
              </label>
              <Input
                id="snd-usecase"
                placeholder="marketing, 2fa, …"
                value={form.use_case}
                onChange={(e) => setForm((p) => ({ ...p, use_case: e.target.value }))}
                disabled={create.isPending}
              />
            </div>
          </div>

          {/* US-specific hint for non-platform users — they can submit
              the form but the API rejects with us_requires_pool_flow.
              Showing the hint inline (instead of waiting for the API
              error) prevents the "fill it in, click create, get
              rejected" loop. */}
          {!isPlatformAdmin && form.country_code.trim().toUpperCase() === 'US' && (
            <div className="flex gap-2.5 items-start px-3.5 py-2.5 bg-info-bg border border-info-bd rounded-md mb-4">
              <Icon.warn width="16" height="16" className="text-info flex-shrink-0 mt-px" />
              <div className="text-[12.5px] text-text-2 leading-relaxed">
                {t(
                  'senders_us_needs_platform',
                  "US numbers require 10DLC review. Reach out to your provider — they'll add the number from the admin surface and notify you once it's approved.",
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end">
            <Button
              type="submit"
              disabled={
                create.isPending ||
                (!isPlatformAdmin && form.country_code.trim().toUpperCase() === 'US')
              }
            >
              {create.isPending ? (
                <>
                  <Spinner />
                  <span>{t('senders_creating')}</span>
                </>
              ) : (
                <>
                  <span>{t('senders_create_button')}</span>
                  <Icon.arrowRight width="16" height="16" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Card>

      {/* Catalog table */}
      <Card padding="none">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div>
            <h2 className="display text-[16px] font-semibold text-text">
              {t('senders_table_title')}
            </h2>
            <p className="text-[12px] text-text-3 mt-0.5">
              {t('senders_total', { count: list.data?.total ?? 0 })}
            </p>
          </div>
        </div>

        {actionError && (
          <div className="px-5 py-2 bg-danger-bg border-b border-danger-bd text-[13px] text-danger">
            {actionError}
          </div>
        )}

        {list.isLoading ? (
          <div className="p-5 space-y-2.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <div className="p-8 text-center">
            <p className="text-[13px] text-danger">{t('senders_load_error')}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void list.refetch()}>
              {t('senders_retry')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('senders_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('senders_empty_sub')}</p>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', busy && 'opacity-60')}>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('senders_col_value')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('senders_col_type')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('senders_col_country')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('senders_col_status')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('senders_col_added')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('senders_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <SenderRow
                    key={s.id}
                    s={s}
                    lang={lang}
                    t={t}
                    onChangeStatus={handleChangeStatus}
                    onDelete={handleDelete}
                    busy={busy}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
